import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  DatePicker,
  Empty,
  Segmented,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  theme,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  EditOutlined,
  FileExcelOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import {
  ApiService,
  PeriodField,
  Report,
  ReportRow,
  ReportRunResult,
  ReportSummary,
} from "../../api";
import { downloadPost } from "../../utils/downloadPost";
import { useTabTitle } from "../../tabs/tabContext";
import SummaryChart from "./SummaryChart";
import { ResizableHeaderCell, useColumnWidths } from "./resizableColumns";
import {
  Divider,
  Field,
  MetaDot,
  PageHeader,
  Panel,
  SectionLabel,
  Stat,
  space,
} from "./ui";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const PAGE_SIZE = 25;

const formatNumber = (value: number | null | undefined) =>
  value === null || value === undefined
    ? "—"
    : Number.isInteger(value)
    ? value.toLocaleString()
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * Running one saved report over a period.
 *
 * The page reads top-down as one argument: *what am I looking at* (header),
 * *over what slice* (one filter row, scoping everything below it), *what does it
 * say* (the roll-up — headline numbers, then a chart, then the same numbers as a
 * table), *show me the cases* (the rows).
 *
 * Two rules hold the layout together. **One filter row**, above everything it
 * scopes, never per-panel — so there is never a question about which control
 * affects which number. And **no panel inside a panel**: where a boundary is
 * needed inside a surface it is a hairline, not a second border arguing with
 * the first.
 */
const ReportViewPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const [report, setReport] = useState<Report | undefined>();
  const [result, setResult] = useState<ReportRunResult | undefined>();
  const [summary, setSummary] = useState<ReportSummary | undefined>();
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [periodField, setPeriodField] = useState<PeriodField | undefined>();
  const [statuses, setStatuses] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState<number[]>([]);
  const [metricId, setMetricId] = useState<string>("count");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);

  useTabTitle(report?.name);

  const runBody = useCallback(
    () => ({
      date_from: range?.[0] ? range[0].startOf("day").toISOString() : null,
      date_to: range?.[1] ? range[1].endOf("day").toISOString() : null,
      ...(periodField ? { period_field: periodField } : {}),
      ...(statusFilter.length ? { statuses: statusFilter } : {}),
    }),
    [range, periodField, statusFilter]
  );

  const run = useCallback(
    async (nextPage: number) => {
      if (!id) return;
      // Hold the previous render while refetching rather than flashing a
      // skeleton — no layout jump on a filter change.
      setRefreshing(true);
      try {
        const body = runBody();
        const [rows, rollup] = await Promise.all([
          ApiService.apiMainReportsRun(parseInt(id), {
            ...body,
            page: nextPage,
            page_size: PAGE_SIZE,
          }),
          ApiService.apiMainReportsSummary(parseInt(id), body),
        ]);
        setResult(rows);
        setSummary(rollup.grouped ? (rollup as ReportSummary) : undefined);
        if (rollup.grouped) {
          const available = (rollup as ReportSummary).metrics;
          setMetricId((current) =>
            available.some((m) => m.id === current) ? current : available[0].id
          );
        }
      } catch (e: any) {
        message.error(e?.body?.message ?? t("Something went wrong"));
      }
      setRefreshing(false);
      setLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, runBody]
  );

  useEffect(() => {
    (async () => {
      if (!id) return;
      try {
        const loaded = await ApiService.apiMainReportsRetrieve(parseInt(id));
        setReport(loaded);
        setPeriodField(loaded.period_field);
        setStatusFilter(loaded.statuses ?? []);
        setStatuses(
          (await ApiService.apiMainRequestStatusesByTemplateRetrieve(
            loaded.template
          )) as any[]
        );
      } catch (e: any) {
        message.error(e?.body?.message ?? t("Something went wrong"));
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // First run once the report's own defaults are in place; after that on demand.
  useEffect(() => {
    if (report) run(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  const exportToExcel = async () => {
    if (!id || !report) return;
    setExporting(true);
    try {
      await downloadPost(
        `/api/main/reports/${id}/export/`,
        runBody(),
        `${report.name}.xlsx`
      );
    } catch (e: any) {
      message.error(e?.message ?? t("Something went wrong"));
    }
    setExporting(false);
  };

  const userInfo = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo") || "{}")
    : null;
  const isAdmin = Boolean(
    userInfo?.isAdmin ?? userInfo?.is_staff ?? userInfo?.is_superuser
  );

  const rows = result?.results ?? [];
  const total = result?.count ?? rows.length;
  const columnWidths = useColumnWidths(id);

  const tableColumns: ColumnsType<ReportRow> = (result?.columns ?? []).map(
    (column) => ({
      title: column.title,
      dataIndex: column.key,
      key: column.key,
      // Undefined until dragged: columns auto-size to their content first, and
      // a width is only pinned once someone has an opinion about it.
      width: columnWidths.widths[column.key],
      ellipsis: { showTitle: false },
      onHeaderCell: () =>
        ({
          columnKey: column.key,
          onResize: columnWidths.setWidth,
          onResetColumn: columnWidths.clearWidth,
        } as any),
      render: (value: any) => {
        const text = value === null || value === undefined ? "" : String(value);
        return (
          <Tooltip title={text.length > 40 ? text : undefined} placement="topLeft">
            <span>{text}</span>
          </Tooltip>
        );
      },
    })
  );

  /** The chart's table-view twin: every measure at once, not just the plotted one. */
  const summaryTable = summary && (
    <Table
      size="small"
      rowKey="key"
      pagination={false}
      showHeader
      dataSource={summary.buckets}
      columns={[
        {
          title: summary.group_by.title || t("Group"),
          dataIndex: "label",
          key: "label",
          ellipsis: true,
        },
        ...summary.metrics.map((metric) => ({
          title: metric.title,
          dataIndex: ["metrics", metric.id],
          key: metric.id,
          align: "right" as const,
          width: 108,
          render: (value: number | null) => (
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {formatNumber(value)}
            </span>
          ),
        })),
      ]}
    />
  );

  /** A single group is a number, not a chart — the stat row already shows it. */
  const showRollup = Boolean(summary && summary.buckets.length >= 2);

  const headline = summary && (
    <div
      style={{
        display: "flex",
        gap: space.band,
        flexWrap: "wrap",
        alignItems: "flex-start",
      }}
    >
      <Stat label={t("Requests")} value={formatNumber(summary.total)} emphasis />
      <Stat
        label={summary.group_by.title || t("Groups")}
        value={formatNumber(summary.buckets.length)}
      />
      {summary.metrics
        .filter((metric) => metric.fn !== "count")
        .slice(0, 3)
        .map((metric) => {
          const values = summary.buckets
            .map((bucket) => bucket.metrics[metric.id])
            .filter((value): value is number => value !== null && value !== undefined);
          if (!values.length) return null;
          const overall =
            metric.fn === "sum"
              ? values.reduce((a, b) => a + b, 0)
              : metric.fn === "min"
              ? Math.min(...values)
              : metric.fn === "max"
              ? Math.max(...values)
              : values.reduce((a, b) => a + b, 0) / values.length;
          return (
            <Stat
              key={metric.id}
              label={metric.title}
              value={formatNumber(overall)}
              hint={t("across all groups")}
            />
          );
        })}
    </div>
  );

  if (loading) {
    return (
      <>
        <Skeleton active paragraph={{ rows: 1 }} style={{ maxWidth: 420 }} />
        <Panel style={{ marginTop: space.base }}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Panel>
      </>
    );
  }

  // No page cap here, unlike the list and the builder. Those are reading
  // surfaces; this one is a table, and a report of a dozen columns wants every
  // pixel of a wide monitor. Only the roll-up band is held to a measure,
  // because a chart of five bars does not get better at 2000px.
  return (
    <div style={{ opacity: refreshing ? 0.55 : 1, transition: "opacity .15s" }}>
      <PageHeader
        title={report?.name ?? t("Report")}
        meta={
          report && (
            <>
              <Tag style={{ marginInlineEnd: 0 }}>{report.template_name}</Tag>
              {report.description && (
                <>
                  <MetaDot />
                  <span>{report.description}</span>
                </>
              )}
              <MetaDot />
              <span>{t("{{count}} columns", { count: report.columns.length })}</span>
            </>
          )
        }
        actions={
          <>
            {isAdmin && report && (
              <Button
                icon={<EditOutlined />}
                onClick={() => navigate(`/reports/${report.id}/edit`)}
              >
                {t("Edit")}
              </Button>
            )}
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              loading={exporting}
              disabled={!report}
              onClick={exportToExcel}
            >
              {t("Export to Excel")}
            </Button>
          </>
        }
      />

      {/* One filter row, scoping everything below it. */}
      <Panel padding={space.snug} style={{ marginBottom: space.base }}>
        <div
          style={{
            display: "flex",
            gap: space.base,
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <Field label={t("Period")}>
            <RangePicker
              value={range as any}
              onChange={(values) => setRange(values as any)}
              allowEmpty={[true, true]}
              presets={[
                {
                  label: t("This month"),
                  value: [dayjs().startOf("month"), dayjs().endOf("month")],
                },
                {
                  label: t("Last month"),
                  value: [
                    dayjs().subtract(1, "month").startOf("month"),
                    dayjs().subtract(1, "month").endOf("month"),
                  ],
                },
                {
                  label: t("This year"),
                  value: [dayjs().startOf("year"), dayjs().endOf("year")],
                },
              ]}
            />
          </Field>
          <Field label={t("Measured against")}>
            <Select
              style={{ width: 148 }}
              value={periodField}
              onChange={setPeriodField}
              options={[
                { value: "start_date", label: t("Start date") },
                { value: "end_date", label: t("End date") },
              ]}
            />
          </Field>
          <Field
            label={t("Statuses")}
            style={{ flex: "0 1 320px", minWidth: 200 }}
          >
            <Select
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder={t("Every status")}
              optionFilterProp="label"
              options={statuses.map((s: any) => ({ value: s.id, label: s.name }))}
            />
          </Field>
          <Button
            icon={<ReloadOutlined />}
            loading={refreshing}
            onClick={() => {
              setPage(1);
              run(1);
            }}
          >
            {t("Run")}
          </Button>
        </div>
      </Panel>

      {summary && summary.buckets.length > 0 && (
        <Panel style={{ marginBottom: space.base }}>
          <div style={{ maxWidth: 1180 }}>{headline}</div>

          {/* One group is not a chart — it is a number, and the stat above
              already is that number. Drawing a lone bar (or worse, a single
              dot on an empty time axis) says less than the figure and takes
              300px to say it. */}
          {showRollup && (
          <Divider style={{ margin: `${space.base}px 0`, maxWidth: 1180 }} />
          )}

          {/* Capped, and deliberately not stretched to the viewport: a bar
              worth 5 running 800px across a wide monitor is length spent on
              nothing. The rows below are free to use the full width — a table
              genuinely wants it, a chart of five categories does not. */}
          {showRollup && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  summary.buckets.length > 2
                    ? "minmax(0, 1.4fr) minmax(0, 1fr)"
                    : "1fr",
                gap: space.wide,
                alignItems: "start",
                maxWidth: 1180,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: space.snug,
                    marginBottom: space.tight,
                  }}
                >
                  <SectionLabel>
                    {summary.metrics.find((m) => m.id === metricId)?.title}{" "}
                    {t("by")} {summary.group_by.title}
                  </SectionLabel>
                  {/* Measures are switched, never plotted together: two scales
                      on one axis would invent a relationship the data doesn't
                      have. */}
                  {summary.metrics.length > 1 && (
                    <Segmented
                      size="small"
                      value={metricId}
                      onChange={(value) => setMetricId(String(value))}
                      options={summary.metrics.map((metric) => ({
                        label: metric.title,
                        value: metric.id,
                      }))}
                    />
                  )}
                </div>
                <SummaryChart summary={summary} metricId={metricId} />
              </div>

              {summary.buckets.length > 2 && (
                <div style={{ minWidth: 0 }}>
                  <SectionLabel style={{ marginBottom: space.tight }}>
                    {t("The numbers")}
                  </SectionLabel>
                  {summaryTable}
                </div>
              )}
            </div>
          )}
        </Panel>
      )}

      <Panel padding={0}>
        <div
          style={{
            padding: `${space.snug}px ${space.base}px`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: space.snug,
          }}
        >
          <SectionLabel>{t("Requests")}</SectionLabel>
          <Space size={space.snug}>
            {/* Only offered once there is something to undo — and a way back
                matters here, because a column dragged to 64px is otherwise a
                trap that survives a reload. */}
            {columnWidths.customised && (
              <Button type="link" size="small" onClick={columnWidths.reset}>
                {t("Reset column widths")}
              </Button>
            )}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("{{count}} rows", { count: total })}
            </Text>
          </Space>
        </div>
        <Divider />
        {rows.length === 0 ? (
          <div style={{ padding: space.band }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div style={{ maxWidth: 380, margin: "0 auto" }}>
                  <div>{t("Nothing matched this period.")}</div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t(
                      "You only ever see rows you have permission to see, so a colleague may get a different count."
                    )}
                  </Text>
                </div>
              }
            />
          </div>
        ) : (
          <Table<ReportRow>
            size="small"
            rowKey="request_id"
            columns={tableColumns}
            dataSource={rows}
            // Drag the trailing edge of any heading; double-click it to hand
            // that column back to auto-sizing.
            components={{ header: { cell: ResizableHeaderCell } }}
            scroll={{ x: "max-content" }}
            onRow={(row) => ({
              onClick: () => navigate(`/requests/${row.request_id}`),
              style: { cursor: "pointer" },
            })}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total,
              showSizeChanger: false,
              hideOnSinglePage: true,
              style: { paddingInline: space.base },
              onChange: (nextPage) => {
                setPage(nextPage);
                run(nextPage);
              },
            }}
            style={{ borderTop: `0px solid ${token.colorSplit}` }}
          />
        )}
      </Panel>
    </div>
  );
};

export default ReportViewPage;
